import { createSelector } from '@reduxjs/toolkit';
import merge from 'lodash.merge';
import Organization, { CapstoneAudioSettings } from '../types/Organization';
import { selectUser } from './selectUser';

const defaultCapstoneAudioSettings: Required<CapstoneAudioSettings> = {
  calls: {
    sound_on: true,
    tone: 'xyloToggle',
  },
  alerts: {
    sound_on: true,
    tone: 'glassVibes',
    repeat: true,
  },
  sms: {
    sound_on: true,
    tone: 'tripleVibe',
    follow_up: {
      active_participants: {
        sound_on: true,
        tone: 'tripleVibe',
      },
      everyone_else: {
        sound_on: false,
        tone: 'tripleVibe',
      },
    },
  },
  tt911: {
    sound_on: true,
    tone: 'tripleVibe',
    repeat: true,
    repeat_times: 2,
  },
};

const defaultUserAudioSettings = {
  calls: {
    volume: 0.7,
  },
  alerts: {
    volume: 0.7,
  },
  sms: {
    volume: 0.7,
  },
  tt911: {
    volume: 0.7,
  },
};

const defaultSettingForApp = {
  capstone: defaultCapstoneAudioSettings,
};

export const selectOrgAudioSettings = (
  app: keyof Required<Organization>['attributes'],
) => {
  return createSelector([selectUser], user => {
    return merge(
      {},
      defaultSettingForApp[app],
      user.currentOrg?.attributes?.[app]?.audio_settings,
    ) as Required<CapstoneAudioSettings>;
  });
};

export const selectUserAudioSettings = createSelector([selectUser], user => {
  return merge(
    {},
    defaultUserAudioSettings,
    user.profile?.attributes?.capstone?.audio_settings,
  );
});

export function selectCapstoneAudioSettingsForFeature<
  T extends keyof CapstoneAudioSettings,
>(feature: T) {
  return createSelector([selectOrgAudioSettings('capstone')], settings => {
    return settings[feature];
  });
}

export function selectUserAudioSettingsForFeature<
  T extends keyof typeof defaultUserAudioSettings,
>(feature: T) {
  return createSelector([selectUserAudioSettings], settings => {
    return settings[feature];
  });
}
